import { MapPin, ShieldAlert } from 'lucide-react';
import { Navigate } from 'react-router';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { TableSkeleton } from '../../../../../shared/components/TableSkeleton';
import { useToast } from '../../../../../shared/hooks/use-toast';
import { useZoneKinds } from '../../../../reference-data/features/zone-kinds/public/use-zone-kinds';
import { buildZoneColumns } from './build-zone-columns';
import { useZoneList } from '../hooks/use-zone-list';
import { useZoneNavigation } from '../../../hooks/use-zone-navigation';
import { deactivateZone, reactivateZone, type ZoneListItem } from '../../../zones.api';
import { ZoneSheet } from './ZoneSheet';
import { ZonesTable } from './ZonesTable';

/**
 * The zones as a server-paged table. A row opens its zone in a wide sheet over the table (`openZoneId`), with
 * a map of the zones around it, and so does New zone from here.
 */
export function ZonesTableView({ openZoneId }: { openZoneId?: string }) {
  const zones = useZoneList();
  const zoneKinds = useZoneKinds();
  const navigation = useZoneNavigation('list');
  const toast = useToast();

  if (zones.state.status === 'forbidden' || zoneKinds.state.status === 'forbidden') return <Navigate to="/" replace />;

  async function setActive(row: ZoneListItem, isActive: boolean): Promise<void> {
    try {
      await (isActive ? reactivateZone(row.id) : deactivateZone(row.id));
    } catch {
      toast.error(`Couldn't ${isActive ? 'reactivate' : 'deactivate'} ${row.name}`);
      return;
    }
    await zones.reload();
    toast.success(`${row.name} ${isActive ? 'reactivated' : 'deactivated'}`);
  }

  const columns = buildZoneColumns({
    list: zones.list,
    zoneKinds: zoneKinds.state.status === 'loaded' ? zoneKinds.state.kinds : [],
    zoneLink: navigation.zoneLink,
    onEdit: (row) => navigation.openZone(row.id),
    onDeactivate: (row) => setActive(row, false),
    onReactivate: (row) => setActive(row, true),
  });

  return (
    <>
      <TableBody zones={zones} columns={columns} onOpen={(row) => navigation.openZone(row.id)} />
      {openZoneId && <ZoneSheet zoneId={openZoneId} onTableChanged={zones.reload} />}
    </>
  );
}

interface TableBodyProps {
  zones: ReturnType<typeof useZoneList>;
  columns: ReturnType<typeof buildZoneColumns>;
  onOpen: (row: ZoneListItem) => void;
}

function TableBody({ zones, columns, onOpen }: TableBodyProps) {
  if (zones.state.status === 'error') {
    return <EmptyState icon={ShieldAlert} title="Couldn't load zones" description="Something went wrong loading the zones list. Try reloading the page." />;
  }
  if (zones.state.status !== 'loaded') return <TableSkeleton label="Loading zones" withSearch />;
  if (zones.state.page.total === 0 && !zones.list.isFiltered) {
    return <EmptyState icon={MapPin} title="No zones yet" description="Add the ports, depots, borders and customer sites this deployment's trucks go to." />;
  }
  return <ZonesTable page={zones.state.page} columns={columns} list={zones.list} isRefreshing={zones.isRefreshing} onOpen={onOpen} />;
}
